import React from 'react';
import { Chip } from '../attendance/shared';
import { d2, n, naira, pct, period, signed, tone } from './shared';

// Working History tabs that are plain tables (Stephanet's workhistory/sections.jsx): each column is [header, value for export,
// optional cell for screen]

const yes = (v, icon = '✓') => (v ? `${icon} Yes` : '—')
const badge = (v, g, a, r) => (v ? <Chip tone={tone(v, g, a, r)}>{v}</Chip> : '—')
const LEAVE_STATUS = { approved: 'Approved', pre_approved: 'Supervisor Approved', pending: 'Pending', rejected: 'Rejected', returned: 'Returned', closed: 'Closed' }

export const SECTIONS = {
  employment: {
    title: 'Employment History', rows: d => d.employment, empty: 'No employment records.',
    cols: [
      ['Start Date', r => d2(r.date)], ['Type', r => r.type || '—'], ['Designation', r => r.designation || '—', r => <b>{r.designation || '—'}</b>],
      ['Grade', r => r.grade || '—'], ['Dept', r => r.dept || '—'], ['Branch', r => r.branch || '—'], ['Supervisor', r => r.supervisor || '—'],
      ['Status', r => r.status, r => badge(r.status, ['Active', 'Confirmed'], ['On Probation', 'Suspended'], ['Terminated'])],
      ['Confirmed', r => d2(r.confirmed)], ['Probation', r => (r.probation ? `${r.probation} months` : '—')], ['Reason', r => r.reason || '—'], ['Action By', r => r.by || '—'],
    ],
  },
  promotions: {
    title: 'Promotions & Transfers', rows: d => d.promotions, empty: 'No promotions or transfers recorded.',
    cols: [
      ['Effective Date', r => d2(r.date)], ['Change Type', r => r.type, r => badge(r.type, ['Promotion', 'Grade Change', 'Designation Change'], ['Transfer', 'Department Transfer', 'Branch Transfer', 'Project Transfer', 'Project Assignment'], ['Demotion'])],
      ['Prev Designation', r => r.prev_designation || '—'], ['New Designation', r => r.new_designation || '—', r => <b className="wh-new">{r.new_designation || '—'}</b>],
      ['Prev Grade', r => r.prev_grade || '—'], ['New Grade', r => r.new_grade || '—', r => <b className="wh-new">{r.new_grade || '—'}</b>],
      ['Prev Dept', r => r.prev_dept || '—'], ['New Dept', r => r.new_dept || '—'],
      ['Salary Impact', r => signed(r.salary_impact), r => <span className={r.salary_impact > 0 ? 'wh-pos' : r.salary_impact < 0 ? 'wh-neg' : ''}>{signed(r.salary_impact)}</span>],
      ['Approved By', r => r.approved_by || '—'], ['Remarks', r => r.remarks || '—'],
    ],
  },
  performance: {
    title: 'Performance', rows: d => d.performance, empty: 'No performance reviews found.',
    cols: [
      ['Period', r => r.period || '—', r => <b>{r.period || '—'}</b>], ['Date', r => d2(r.date)], ['KPI', r => pct(r.kpi)], ['Competency', r => pct(r.competency)],
      ['Attendance', r => pct(r.attendance)], ['Behaviour', r => pct(r.behaviour)], ['Technical', r => pct(r.technical)],
      ['Overall', r => r.overall, r => <b className={r.overall >= 80 ? 'wh-pos' : r.overall >= 60 ? 'wh-amb' : 'wh-neg'}>{r.overall}</b>],
      ['Rating', r => r.rating, r => badge(r.rating, ['Exceeds Expectations', 'Outstanding', 'Very Good'], ['Meets Expectations', 'Good'], ['Below Expectations', 'Needs Improvement', 'Unsatisfactory'])],
      ['Reviewer', r => r.reviewer || '—'], ['Promo Rec.', r => yes(r.promo, '✅')], ['Training Rec.', r => yes(r.training, '📚')],
    ],
  },
  attendance: {
    title: 'Attendance Summary', rows: d => d.attendance, empty: 'No attendance recorded.',
    cols: [
      ['Period', r => period(r.period), r => <b>{period(r.period)}</b>], ['Working Days', r => n(r.working)], ['Present', r => n(r.present)],
      ['Missed', r => n(r.missed), r => <span className={r.missed ? 'wh-neg' : ''}>{n(r.missed)}</span>], ['Approved Missed', r => n(r.approved)],
      ['Declined', r => n(r.declined), r => <span className={r.declined ? 'wh-neg' : ''}>{n(r.declined)}</span>],
      ['Lateness', r => n(r.late), r => <span className={r.late ? 'wh-amb' : ''}>{n(r.late)}</span>], ['Overtime Hrs', r => n(r.ot)], ['Leave Days', r => n(r.leave)],
      ['Deduction', r => naira(r.deduction), r => <span className={r.deduction ? 'wh-neg' : ''}>{r.deduction ? naira(r.deduction) : '₦0'}</span>],
      ['Payroll Status', r => r.payroll, r => badge(r.payroll, ['Posted', 'No Deduction'], ['Pending'])],
    ],
  },
  leave: {
    title: 'Leave History', rows: d => d.leave, empty: 'No leave records for this employee.',
    cols: [
      ['Year', r => r.year], ['Leave Type', r => `${r.type} Leave`, r => <b>{r.type} Leave</b>], ['Entitlement', r => n(r.entitlement)], ['Used', r => n(r.used)],
      ['Balance', r => n(r.balance), r => <b className={r.balance <= 0 ? 'wh-neg' : 'wh-pos'}>{n(r.balance)}</b>], ['Start', r => d2(r.start)], ['End', r => d2(r.end)],
      ['Days', r => n(r.days)], ['Status', r => LEAVE_STATUS[r.status] || r.status, r => badge(LEAVE_STATUS[r.status] || r.status, ['Approved', 'Closed'], ['Pending', 'Supervisor Approved', 'Returned'], ['Rejected'])],
      ['Approved By', r => r.approved_by || '—'], ['Payroll Impact', r => r.payroll],
    ],
  },
  overtime: {
    title: 'Overtime', rows: d => d.overtime, empty: 'No overtime records found for this employee.',
    cols: [
      ['Date', r => d2(r.date)], ['Record ID', r => r.reference || '—'], ['Hours', r => n(r.hours)], ['Type', r => (r.type || '').replace(/^\w/, c => c.toUpperCase())],
      ['Amount (est.)', r => naira(r.amount)], ['Reason / Work Done', r => r.reason || '—'],
      ['Status', r => r.status, r => badge((r.status || '').replace(/^\w/, c => c.toUpperCase()), ['Approved', 'Closed'], ['Pending'], ['Rejected'])], ['Approved By', r => r.approved_by || '—'],
    ],
  },
  incidents: {
    title: 'Incidents & Discipline', rows: d => d.incidents, empty: 'No incidents or disciplinary records.',
    cols: [
      ['Date', r => d2(r.date)], ['Type', r => r.type, r => badge(r.type, [], ['Policy Breach', 'Misconduct', 'Negligence', 'Lateness', 'Absence', 'Tool Repair', 'Tool Downtime'], ['Warning', 'Disciplinary', 'Fraud / Theft', 'Dismissal', 'Tool Missing', 'Tool Damage', 'Suspension'])],
      ['Severity', r => r.severity, r => badge(r.severity, ['Minor'], ['Moderate'], ['Severe', 'Critical'])], ['Description', r => r.description || '—', r => <span className="wh-clip" title={r.description}>{r.description || '—'}</span>],
      ['Warning?', r => yes(r.warning, '⚠️')], ['Query?', r => yes(r.query, '📋')], ['Suspension?', r => yes(r.suspension, '🚫')], ['Payroll Impact', r => r.payroll || 'None'],
      ['Status', r => r.status, r => badge(r.status, ['Resolved'], ['Monitoring'], ['Open'])], ['Action By', r => r.by || '—'], ['Source', r => r.source],
    ],
  },
  training: {
    title: 'Training & Certification', rows: d => d.training, empty: 'No training or certification recorded. Use Add Training to record one.',
    cols: [
      ['Date', r => d2(r.date)], ['Title', r => r.title, r => <b>{r.title}</b>], ['Type', r => r.type || '—'], ['Provider', r => r.provider || '—'], ['Location', r => r.location || '—'],
      ['Cost', r => naira(r.cost)], ['Certificate?', r => yes(r.certificate, '✅')], ['Cert No.', r => r.cert_no || '—'],
      ['Expiry', r => d2(r.expiry), r => <span className={r.expiry && r.expiry < new Date().toISOString().slice(0, 10) ? 'wh-neg' : ''}>{d2(r.expiry)}</span>],
      ['Renewal Req?', r => yes(r.renewal, '⚠️')], ['Status', r => r.status, r => badge(r.status, ['Completed'], ['In Progress', 'Pending', 'Approved'], ['Rejected', 'Failed'])], ['Impact', r => r.impact || '—'],
    ],
  },
  benefits: {
    title: 'Benefits', rows: d => d.benefits, empty: 'No benefits recorded.',
    cols: [
      ['Benefit Type', r => r.type, r => <b>{r.type}</b>], ['Effective Date', r => d2(r.date)], ['Amount', r => naira(r.amount)], ['Frequency', r => r.frequency || '—'],
      ['Status', r => r.status, r => badge(r.status, ['Active'], ['Pending', 'Suspended'], ['Ended'])], ['Added By', r => r.by || '—'], ['Payroll Impact', r => r.payroll],
      ['End Date', r => (r.end ? d2(r.end) : 'Ongoing')], ['Reason', r => r.reason || '—'],
    ],
  },
  assets: {
    title: 'Assets / Tools / PPE', rows: d => d.assets, empty: 'No assets, tools or PPE issued.',
    cols: [
      ['Issue Date', r => d2(r.date)], ['Type', r => r.type], ['Name', r => r.name, r => <b>{r.name}</b>], ['Code', r => r.code || '—'], ['Serial', r => r.serial || '—'],
      ['Qty', r => n(r.qty)], ['Condition Issued', r => r.condition || '—'], ['Expected Return', r => d2(r.expected)], ['Return Date', r => d2(r.returned)],
      ['Returned Condition', r => r.returned_condition || '—'], ['Damage', r => r.damage || 'None'],
      ['Status', r => r.status, r => badge(r.status, ['Active', 'Returned'], [], ['Damaged', 'Lost'])],
    ],
  },
  payroll: {
    title: 'Payroll & Salary', rows: d => d.payroll, empty: 'No salary history.',
    cols: [
      ['Effective', r => d2(r.date)], ['Month', r => r.month || '—'], ['Prev Salary', r => naira(r.prev)], ['New Salary', r => naira(r.new), r => <b>{naira(r.new)}</b>],
      ['Change', r => r.change, r => badge(r.change, ['Promotion', 'Annual Review', 'New Hire Salary', 'Market Adjustment'], ['Grade Realignment', 'Salary Correction', 'Payroll Adjustment', 'Current Salary'])],
      ['Allowance', r => r.allowance || '—'], ['Deduction', r => r.deduction || '—'], ['Reason', r => r.reason || '—'], ['Approved By', r => r.approved_by || '—'],
      ['Status', r => r.status, r => badge(r.status, ['Posted'], ['Pending'], ['Rejected'])],
    ],
  },
  notes: {
    title: 'Management Notes', rows: d => d.notes, empty: 'No management notes. Use Add Note to record one.',
    cols: [
      ['Date', r => d2(r.date)], ['Type', r => r.type, r => <Chip tone="purple">{r.type}</Chip>], ['Subject', r => r.subject, r => <b>{r.subject}</b>],
      ['Note', r => r.note || '—', r => <span className="wh-clip" title={r.note}>{r.note || '—'}</span>], ['Confidentiality', r => r.confidentiality], ['Added By', r => r.by || '—'],
      ['Visible To', r => r.visible_to || '—'], ['Action Required?', r => yes(r.action, '⚠️')], ['Status', r => r.status, r => badge(r.status, ['Active', 'Closed'], ['Pending'], ['Overdue'])],
    ],
  },
  audit: {
    title: 'Audit Trail', rows: d => d.audit, empty: 'No audit entries for this employee yet.',
    cols: [
      ['Date & Time', r => (r.at || '').replace('T', ' ').slice(0, 19), r => <span className="nowrap">{(r.at || '').replace('T', ' ').slice(0, 19)}</span>],
      ['User', r => r.user], ['Role', r => r.role], ['Action', r => r.action, r => <b>{r.action}</b>], ['Module', r => r.module],
      ['Prev Value', r => r.prev || '—', r => <span className="wh-prev">{r.prev || '—'}</span>], ['New Value', r => r.new || '—', r => <span className="wh-new">{r.new || '—'}</span>],
      ['Comment', r => r.comment || '—'],
    ],
  },
}

export const DOC_COLS = [
  ['Type', r => r.type], ['Title', r => r.title], ['Uploaded', r => d2(r.uploaded)], ['Expiry', r => d2(r.expiry)], ['Status', r => r.status],
  ['Uploaded By', r => r.by], ['Confidentiality', r => r.confidentiality],
]
